import React, { useState, useContext, useMemo } from "react";
import { Box, Typography, Button, Stack } from "@mui/material";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import AuturaTextField from "components/inputs/AuturaTextField";
import { useTranslation } from "react-i18next";
import { DataWidget, WIZARD_ACTION } from "types";
import { ImportFieldInput } from "./ImportFieldInput";
import { ImportWizardContext } from "components/importer/ImportWizardContext";
dayjs.extend(customParseFormat);

interface ValidationWidgetProps {
  setDataWidget: (value: DataWidget) => void;
}

export const ValidationWidget: React.FC<ValidationWidgetProps> = ({
  setDataWidget,
}) => {
  const { t } = useTranslation();
  const { state, dispatch, fieldMetadata, rowsWithInvalidValues } =
    useContext(ImportWizardContext);
  const [newValue, setNewValue] = useState("");

  // this component is not used if the user has not selected a system field
  if (!state.selectedSystemField) {
    setDataWidget("none");
    return null;
  }

  const systemField = state.selectedSystemField;
  const csvField = state.columnMappings[systemField] || "";
  const { validator } = fieldMetadata[systemField];

  const currentRowIndex = rowsWithInvalidValues.length
    ? rowsWithInvalidValues[0]
    : -1;

  const currentRowNumber = currentRowIndex
    ? `${currentRowIndex + (state.csvUsesTemplate ? 1 : 2)}`
    : "";

  const originalValue = useMemo(() => {
    if (currentRowIndex >= 0 && csvField) {
      return state.modifiedData[currentRowIndex][csvField];
    }
    return "";
  }, [currentRowIndex, csvField]);

  const handleReplaceAll = (
    csvField = "",
    valueToReplace: string,
    newValue: string,
  ) => {
    const updatedCsvData = state.modifiedData.map((row) => {
      if (
        row[csvField]?.toLowerCase()?.trim() ===
        valueToReplace?.toLowerCase()?.trim()
      ) {
        row[csvField] = newValue;
      }
      return row;
    });

    dispatch({
      type: WIZARD_ACTION.SET_MODIFIED_DATA,
      payload: updatedCsvData,
    });
  };

  const handleUpdateValue = (
    rowIndex: number,
    csvField = "",
    value: string,
  ) => {
    const updatedData = [...state.modifiedData];
    updatedData[rowIndex][csvField] = value;
    dispatch({
      type: WIZARD_ACTION.SET_MODIFIED_DATA,
      payload: updatedData,
    });
  };

  const replaceAllDisabled = !validator(newValue) || !originalValue;

  return (
    <Stack direction="column" spacing={0}>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="subtitle1" textAlign="left" pb={2}>
          {`${currentRowNumber ? `${t("row")} ${currentRowNumber}` : ""}`}
        </Typography>
        <Typography variant="subtitle1" textAlign="right" pb={2}>
          {t("remaining_errors", {
            count: rowsWithInvalidValues.length,
            ns: "importWizard",
          })}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={3} display="flex" pb={2}>
        <Box sx={{ flex: 1 }}>
          <ImportFieldInput
            field={systemField}
            value={newValue}
            setValue={setNewValue}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <AuturaTextField
            name="originalValue"
            label={t("original_value", { ns: "importWizard" })}
            value={originalValue}
            fullWidth
            readonly
          />
        </Box>
      </Stack>
      <Stack
        direction="row"
        gap={4}
        display="flex"
        justifyContent="space-between"
      >
        <Button
          onClick={() => handleUpdateValue(currentRowIndex, csvField, newValue)}
          variant="text"
          color="primary"
          disabled={!validator(newValue)}
        >
          {t("update_value", { ns: "importWizard" })}
        </Button>
        <Button
          onClick={() => handleReplaceAll(csvField, originalValue, newValue)}
          variant="text"
          color="primary"
          disabled={replaceAllDisabled}
        >
          {t("replace_all", { ns: "importWizard" })}
        </Button>
      </Stack>
    </Stack>
  );
};
